import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { AppRoutes, SESSION_POST_LOGIN_CHAT_RESET_PENDING } from '@/constants';
import { LoginPage, MicrosoftLoginRedirect } from './pages/LoginPage';
import { lazy, Suspense, useEffect, useRef, type ReactElement } from 'react';
import { LoadingSpinner, SkipLink } from '@repo/atomic-components';
import { setNavigateMiddleware } from '@/api/core/navigationMiddleware';
import {
  useCheckLoginStatus,
  useFocusMainOnRouteChange,
  useNewBuildDetector,
  usePreventUnloadWhenLoading,
} from '@/hooks';
import { NewVersionBanner } from './components/NewVersionBanner';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import { useRouteTitle } from '@/hooks';
import { useLazyGetUserAboutQuery } from '@/api/users/userAbout';
import { clearChat, selectUserAboutData, useAppDispatch, useAppSelector } from '@/store';
import { stopActiveDictationSession } from '@/ui/features/chat/dictationSessionRegistry';
import { ChatPage } from '@/ui/pages/ChatPage';
import { SidebarLayout } from '@/ui/templates/SidebarLayout';
import { useVisibleActions } from '@/ui/pages/ActionsPage/useVisibleActions';
// From its own module: the `CasePage` barrel would undo the page's lazy route.
import { useTranscriptionOutcomeToasts } from '@/ui/pages/CasePage/hooks/useTranscriptionOutcomeToasts';
import { useAccessControl } from '@/hooks';
import { AccessControlAction, ADMIN_TOOLS_ALLOWED_USER_IDS } from '@/constants';

/** Routes are code-split, except auth and chat (the default landing page). */
const CaseListPage = lazy(() => import('@/ui/pages/CaseListPage').then((m) => ({ default: m.CaseListPage })));
const ProjectListPage = lazy(() => import('@/ui/pages/ProjectListPage').then((m) => ({ default: m.ProjectListPage })));
const ProjectPage = lazy(() => import('@/ui/pages/ProjectPage').then((m) => ({ default: m.ProjectPage })));
const ProjectChatHistoryPage = lazy(() =>
  import('@/ui/pages/ProjectChatHistoryPage').then((m) => ({ default: m.ProjectChatHistoryPage })),
);
const ProjectChatPage = lazy(() => import('@/ui/pages/ProjectChatPage').then((m) => ({ default: m.ProjectChatPage })));
const CasePage = lazy(() => import('@/ui/pages/CasePage').then((m) => ({ default: m.CasePage })));
const HistoryPage = lazy(() => import('@/ui/pages/HistoryPage').then((m) => ({ default: m.HistoryPage })));
const CaseHistoryPage = lazy(() => import('@/ui/pages/CaseHistoryPage').then((m) => ({ default: m.CaseHistoryPage })));
const AerialsListPage = lazy(() => import('@/ui/pages/AerialsListPage').then((m) => ({ default: m.AerialsListPage })));
const AerialPage = lazy(() => import('@/ui/pages/AerialPage').then((m) => ({ default: m.AerialPage })));
const AerialCaseListPage = lazy(() =>
  import('@/ui/pages/AerialCaseListPage').then((m) => ({ default: m.AerialCaseListPage })),
);
const DocumentViewerPage = lazy(() =>
  import('@/ui/pages/DocumentViewerPage').then((m) => ({ default: m.DocumentViewerPage })),
);
const AdminToolsPage = lazy(() => import('@/ui/pages/AdminToolsPage').then((m) => ({ default: m.AdminToolsPage })));
const ActionsPage = lazy(() => import('@/ui/pages/ActionsPage/ActionsPage').then((m) => ({ default: m.ActionsPage })));
const UsersPage = lazy(() => import('@/ui/pages/UserManagementPage').then((m) => ({ default: m.UsersPage })));
const HowWeFletchPage = lazy(() => import('@/ui/pages/HowWeFletchPage').then((m) => ({ default: m.HowWeFletchPage })));

const RouteFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <LoadingSpinner />
  </div>
);

const GlobalAdminRoute = ({ children }: { children: ReactElement }) => {
  const isGlobalAdmin = useAccessControl(AccessControlAction.GLOBAL_ADMIN_ONLY);
  const userAboutData = useAppSelector(selectUserAboutData);
  return isGlobalAdmin || (!!userAboutData?.userId && ADMIN_TOOLS_ALLOWED_USER_IDS.includes(userAboutData.userId)) ? (
    children
  ) : (
    <Navigate to={`/${AppRoutes.Chat}`} replace />
  );
};

const ActionsRoute = ({ children }: { children: ReactElement }) => {
  const visibleActions = useVisibleActions();
  return visibleActions.length > 0 ? children : <Navigate to={`/${AppRoutes.Chat}`} replace />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const prevPathnameRef = useRef(pathname);
  const dispatch = useAppDispatch();
  usePreventUnloadWhenLoading();
  useCheckLoginStatus();
  useRouteTitle();
  useFocusMainOnRouteChange();
  const { isNewVersionAvailable } = useNewBuildDetector();

  const userAboutData = useAppSelector(selectUserAboutData);
  const [triggerGetUserAbout, { isLoading: isUserAboutLoading, isUninitialized: isUserAboutUninitialized }] =
    useLazyGetUserAboutQuery();
  useTranscriptionOutcomeToasts(Boolean(userAboutData));

  useEffect(() => {
    triggerGetUserAbout({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setNavigateMiddleware(navigate);
  }, [navigate]);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    stopActiveDictationSession();
  }, [pathname]);

  useEffect(() => {
    // Only after OAuth (see MicrosoftLoginRedirect), not on every authenticated page load/refresh.
    if (!userAboutData?.userId) return;
    if (sessionStorage.getItem(SESSION_POST_LOGIN_CHAT_RESET_PENDING) !== '1') return;

    sessionStorage.removeItem(SESSION_POST_LOGIN_CHAT_RESET_PENDING);
    dispatch(clearChat());
    navigate(`/${AppRoutes.Chat}`, { replace: true });
  }, [dispatch, navigate, userAboutData?.userId]);

  if (!userAboutData && !pathname.includes(AppRoutes.Login) && !pathname.includes(AppRoutes.LoginRedirect)) {
    if (isUserAboutUninitialized || isUserAboutLoading) return <RouteFallback />;
    return <Navigate to={`/${AppRoutes.Login}`} replace />;
  }

  return (
    <>
      <SkipLink />
      {isNewVersionAvailable && <NewVersionBanner />}
      <RouteErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path={AppRoutes.Login} element={<LoginPage />} />
            <Route path={AppRoutes.LoginRedirect} element={<MicrosoftLoginRedirect />} />
            <Route element={<SidebarLayout />}>
              <Route path={`${AppRoutes.Chat}/:chatId?`} element={<ChatPage />} />
              <Route path={AppRoutes.History} element={<HistoryPage />} />
              <Route path={`${AppRoutes.CaseHistory}/:caseId`} element={<CaseHistoryPage />} />
              <Route path={AppRoutes.Cases} element={<CaseListPage />} />
              <Route path={`${AppRoutes.Cases}/:caseId`} element={<CasePage />} />
              <Route path={AppRoutes.Projects} element={<ProjectListPage />} />
              <Route path={`${AppRoutes.Projects}/:projectId`} element={<ProjectPage />} />
              <Route
                path={`${AppRoutes.Projects}/:projectId/${AppRoutes.ProjectChatHistory}`}
                element={<ProjectChatHistoryPage />}
              />
              <Route
                path={`${AppRoutes.Projects}/:projectId/${AppRoutes.Chat}/:chatId?`}
                element={<ProjectChatPage />}
              />
              <Route path={AppRoutes.Aerials} element={<AerialsListPage />} />
              <Route path={`${AppRoutes.Aerials}/:aerialId/runs/:runId`} element={<AerialCaseListPage />} />
              <Route path={`${AppRoutes.Aerials}/:aerialId`} element={<AerialPage />} />
              <Route
                path={AppRoutes.Actions}
                element={
                  <ActionsRoute>
                    <ActionsPage />
                  </ActionsRoute>
                }
              />
              <Route
                path={AppRoutes.AdminTools}
                element={
                  <GlobalAdminRoute>
                    <AdminToolsPage />
                  </GlobalAdminRoute>
                }
              />
              <Route path={AppRoutes.UserManagement} element={<UsersPage />} />
            </Route>
            <Route path={`${AppRoutes.DocumentViewer}/:documentId`} element={<DocumentViewerPage />} />
            <Route path={AppRoutes.HowWeFletch} element={<HowWeFletchPage />} />
            <Route path="*" element={<Navigate to={`/${AppRoutes.Chat}`} replace />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </>
  );
}

export default App;
